import axios from "axios";
import { useState, useEffect, useContext } from "react";

import { UserAuth } from "../../utils/AuthContext";
import { DataContext } from "../../utils/DataContext";

import { useTranslation } from "react-i18next";
import { onlyNums } from "../../utils/OnlyNums";

import infoIcon from "../../assets/information.png";

import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import PageWrapper from "../../components/PageWrapper";

import ProductInfoModal from "./ProductInfoModal";
import Dropdown from "../../components/Dropdown";
import useDebounce from "../../utils/useDebounce";

export const MEASURING_UNITS = Object.freeze([
  { title: "piece", value: "PIECE" },
  { title: "kg", value: "KG" },
  { title: "gram", value: "GRAM" },
  { title: "meter", value: "METER" },
  { title: "liter", value: "LITER" },
  { title: "bottle", value: "BOTTLE" },
  { title: "box", value: "BOX" },
  { title: "pack", value: "PACK" },
]);

const Inventory = () => {
  const { userData } = useContext(UserAuth);
  const { t } = useTranslation("general");

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [productsDetails, setProductsDetails] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [newItem, setNewItem] = useState({
    barcode: "",
    name: "",
    purchasePrice: null,
    price: null,
    quantityInStock: null,
  });

  const currencies = [
    { title: "iqd", value: "IQD" },
    { title: "usd", value: "USD" },
  ];

  const [selectedUnit, setSelectedUnit] = useState({
    title: "piece",
    value: "piece",
  });

  const [purchaseCurrency, setPurchaseCurrency] = useState({
    title: "iqd",
    value: "IQD",
  });

  const [sellingCurrency, setSellingCurrency] = useState({
    title: "iqd",
    value: "IQD",
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [isProductsVisible, setIsProductsVisible] = useState(true);

  const [selectedRow, setSelectedRow] = useState(false);

  const handleInputChange = (e) => {
    setNewItem({ ...newItem, [e.target.name]: e.target.value });
  };

  const handleShowInfoModal = (item) => setSelectedRow(item);

  const inventoryTable = {
    titles: [
      { value: "barcode" },
      { value: "name" },
      { value: "purchasePrice" },
      { value: "price" },
      { value: "quantityInStock" },
      {
        value: "theOptions",
      },
    ],

    rows: inventory.map((item, index) => [
      { value: item.barcode, type: TABLE_CELL_TYPES.TEXT },
      { value: item.name, type: TABLE_CELL_TYPES.TEXT },
      {
        value: `${item.purchasePriceCurrency} ${item.purchasePrice}`,
        type: TABLE_CELL_TYPES.TEXT,
      },
      {
        value: `${item.sellingPriceCurrency} ${item.price}`,
        type: TABLE_CELL_TYPES.TEXT,
      },
      {
        value: `${item.quantityInStock} ${t(
          item?.unitOfMeasure?.toLowerCase()
        )}`,
        type: TABLE_CELL_TYPES.TEXT,
      },
      {
        type: TABLE_CELL_TYPES.ELEMENT,
        value: (
          <CustomButton
            variant="blank"
            title={t("ViewInfo")}
            onClick={() => handleShowInfoModal(productsDetails[index])}
            icon={infoIcon}
            iconSize={15}
          />
        ),
      },
    ]),
  };

  const createProduct = async () => {
    const token = localStorage.getItem("token");

    const organizationId = parseInt(localStorage.getItem("organizationId"));

    const payload = {
      ...newItem,
      unitOfMeasure: selectedUnit?.value?.toUpperCase(),
      sellingPriceCurrency: sellingCurrency?.value,
      purchasePriceCurrency: purchaseCurrency?.value,
      organizationId,
    };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_URL}/createProduct`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      console.log(error);
    }
  };

  const addToInventory = async () => {
    if (
      !newItem.barcode ||
      !newItem.name ||
      !newItem.price ||
      !newItem.purchasePrice ||
      !newItem.quantityInStock
    ) {
      alert("Please fill all fields!");
      return;
    }

    const parsedPurchasedPrice = parseFloat(newItem.purchasePrice);
    const parsedPrice = parseFloat(newItem.price);

    if (isNaN(parsedPurchasedPrice) || isNaN(parsedPrice)) {
      alert("Please enter valid numbers for cost and price.");
      return;
    }

    try {
      await createProduct();
      await getAllProducts();
    } catch (error) {
      console.log(error);
    }

    setNewItem({
      barcode: "",
      name: "",
      purchasePrice: "",
      price: "",
      quantityInStock: "",
    });
  };

  const getAllProducts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getProducts`,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
          },
          params: {
            page: pagination.currentPage || 1,
            limit: 5,
            searchQuery: searchQuery || "",
            categories: userData?.organizationType,
            organizationId: userData?.organizationId,
          },
        }
      );

      if (response.status === 200) {
        const { products, pagination: paginationData } = response.data;
        const inventoryItems = products.map((product) => {
          return {
            barcode: product.barcode,
            name: product.name,
            purchasePrice: product.purchasePrice,
            price: product.price,
            quantityInStock: product.quantityInStock,
            unitOfMeasure: product?.unitOfMeasure,
            purchasePriceCurrency: product?.purchasePriceCurrency,
            sellingPriceCurrency: product?.sellingPriceCurrency,
          };
        });
        setProductsDetails(products);
        setInventory(inventoryItems);

        setPagination(paginationData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getAllProducts();
  }, [pagination.currentPage, debouncedSearch]);

  return (
    <PageWrapper withBackButton>
      <div className="o-column o-column--gap-size_300 u-padding--bottom_500">
        <h2>📦 {t("inventoryManagement")}</h2>
        <div className="o-column o-column--gap-size_100 u-padding--bottom_500">
          <section className="o-row o-row--gap-size_300">
            <div className="o-column-row-switcher o-row--gap-size_300">
              <CustomInput
                name="barcode"
                placeholder={t("barcode")}
                value={newItem.barcode}
                onChange={handleInputChange}
              />
              <CustomInput
                name="name"
                placeholder={t("name")}
                value={newItem.name}
                onChange={handleInputChange}
              />
            </div>
            <div className="o-column-row-switcher o-row--gap-size_300">
              <CustomInput
                name="purchasePrice"
                placeholder={t("costOnMe")}
                onKeyDown={onlyNums}
                value={newItem.purchasePrice}
                onChange={handleInputChange}
              />

              <Dropdown
                options={currencies}
                selected={purchaseCurrency}
                setSelected={setPurchaseCurrency}
                // flex
              />
            </div>
          </section>
          <section className="o-row o-row--gap-size_300">
            <div className="o-column-row-switcher o-row--gap-size_300">
              <CustomInput
                name="price"
                onKeyDown={onlyNums}
                placeholder={t("sellingCost")}
                value={newItem.price}
                onChange={handleInputChange}
              />
              <CustomInput
                type="number"
                name="quantityInStock"
                placeholder={t("quantityInStock")}
                value={newItem.quantityInStock}
                onChange={handleInputChange}
              />
            </div>
            <div className="o-column-row-switcher o-row--gap-size_300">
              <Dropdown
                options={MEASURING_UNITS}
                selected={selectedUnit}
                setSelected={setSelectedUnit}
                // flex
              />
              <Dropdown
                options={currencies}
                selected={sellingCurrency}
                setSelected={setSellingCurrency}
                // flex
              />
            </div>
          </section>
          <div className="o-row o-row--horizontal-align_end u-margin--top_500">
            <CustomButton
              title={t("addToInventory")}
              onClick={addToInventory}
            />
          </div>
        </div>
        <div className="o-row o-row--horizontal-align_space-between toggle-products">
          <div>
            <CustomInput
              placeholder={t("search")}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              resetSearch={() => {
                setSearchQuery("");
              }}
              thick
            />
          </div>
          <CustomButton
            variant="blank"
            title={
              isProductsVisible
                ? `${t("hideProducts")}▲`
                : `${t("showProducts")}▼`
            }
            onClick={() => setIsProductsVisible(!isProductsVisible)}
          />
        </div>
        {isProductsVisible && (
          <RoundedTable
            {...inventoryTable}
            pagination={pagination}
            setPagination={setPagination}
          />
        )}
      </div>

      {selectedRow ? (
        <ProductInfoModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
          currencies={currencies}
          handleClose={() => setSelectedRow(null)}
          getAllProducts={getAllProducts}
        />
      ) : null}
    </PageWrapper>
  );
};

export default Inventory;
